<template>
  <v-card-text class="pa-6">
    <!-- Header and Controls -->
    <div class="d-flex justify-space-between align-center mb-4">
      <h3 class="text-h6">
        <v-icon left color="warning">mdi-clock-alert-outline</v-icon>
        ประวัติการขาดลามาสาย
      </h3>
      <div class="d-flex align-center">
        <v-btn color="primary" @click="$emit('add')" class="mr-3">
          <v-icon left>mdi-plus</v-icon>
          เพิ่มข้อมูล
        </v-btn>
      </div>
    </div>

    <!-- Filter Controls -->
    <v-card outlined class="mb-4">
      <v-card-text class="py-3">
        <v-row align="center">
          <v-col cols="12" md="3">
            <v-select
              v-model="selectedMonth"
              :items="monthOptions"
              label="เดือน"
              outlined
              dense
              clearable
              @change="onFilterChange"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="selectedYear"
              :items="yearOptions"
              label="ปี"
              outlined
              dense
              @change="onFilterChange"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="selectedType"
              :items="typeFilterOptions"
              label="ประเภท"
              outlined
              dense
              clearable
              @change="onFilterChange"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="selectedStatus"
              :items="statusFilterOptions"
              label="สถานะ"
              outlined
              dense
              clearable
              @change="onFilterChange"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <div class="text-caption grey--text">
              <v-icon small class="mr-1">mdi-information-outline</v-icon>
              เลือกตัวกรองเพื่อค้นหาข้อมูล หรือเว้นว่างเพื่อดูทั้งหมด
              • ข้อมูลทั้งหมด: {{ totalRecords }} รายการ
              • แสดง: {{ filteredRecords }} รายการ
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Summary Cards -->
    <v-row class="mb-4">
      <v-col cols="6" md="3" v-for="(item, index) in summaryCards" :key="index">
        <v-card outlined class="text-center pa-3" :class="item.class">
          <v-icon :color="item.color" size="32" class="mb-2">{{ item.icon }}</v-icon>
          <div class="text-caption grey--text">{{ item.label }}</div>
          <div class="text-h6 font-weight-medium" :class="item.textClass">{{ item.value }}</div>
          <div class="text-caption" :class="item.subtextClass" v-if="item.subtext">{{ item.subtext }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="filteredAttendances"
      :loading="loading"
      no-data-text="ไม่มีข้อมูลการขาดลามาสาย"
      :items-per-page="itemsPerPage"
      :page.sync="page"
      hide-default-footer
      class="elevation-1"
      :sort-by="['attendance_date']"
      :sort-desc="[true]"
    >
      <template v-slot:item.attendance_date="{ item }">
        <v-chip small color="primary" text-color="white">
          <v-icon left small>mdi-calendar</v-icon>
          {{ formatDate(item.attendance_date) }}
        </v-chip>
      </template>
      
      <template v-slot:item.attendance_type="{ item }">
        <v-chip 
          small 
          :color="getTypeColor(item.attendance_type)" 
          text-color="white"
        >
          <v-icon left small>{{ getTypeIcon(item.attendance_type) }}</v-icon>
          {{ item.type_label || getTypeLabel(item.attendance_type) }}
        </v-chip>
      </template>
      
      <template v-slot:item.start_time="{ item }">
        <span v-if="item.start_time" class="font-weight-medium">
          {{ formatTime(item.start_time) }}
        </span>
        <span v-else class="grey--text">-</span>
      </template>
      
      <template v-slot:item.actual_time="{ item }">
        <span v-if="item.actual_time" class="font-weight-medium">
          {{ formatTime(item.actual_time) }}
        </span>
        <span v-else class="grey--text">-</span>
      </template>
      
      <template v-slot:item.duration_minutes="{ item }">
        <div v-if="item.duration_minutes && item.duration_minutes > 0">
          <v-chip 
            x-small 
            :color="getDurationColor(item.duration_minutes)"
            text-color="white"
          >
            {{ formatDuration(item.duration_minutes) }}
          </v-chip>
        </div>
        <span v-else class="grey--text">-</span>
      </template>
      
      <template v-slot:item.reason="{ item }">
        <div class="reason-cell">
          <span v-if="item.reason && item.reason.length <= 50">{{ item.reason }}</span>
          <div v-else-if="item.reason">
            <span>{{ item.reason.substring(0, 47) }}...</span>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon x-small v-bind="attrs" v-on="on">
                  <v-icon small>mdi-information-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ item.reason }}</span>
            </v-tooltip>
          </div>
          <span v-else class="grey--text">ไม่ระบุ</span>
        </div>
      </template>
      
      <template v-slot:item.status="{ item }">
        <v-chip 
          small 
          :color="getStatusColor(item.status)" 
          text-color="white"
        >
          <v-icon left small>{{ getStatusIcon(item.status) }}</v-icon>
          {{ item.status_label || getStatusLabel(item.status) }}
        </v-chip>
      </template>
      
      <template v-slot:item.approved_by="{ item }">
        <div v-if="item.approved_by" class="d-flex align-center">
          <v-icon small class="mr-1">mdi-account-check</v-icon>
          {{ item.approved_by }}
        </div>
        <span v-else class="grey--text">-</span>
      </template>
      
      <template v-slot:item.file_path="{ item }">
        <v-btn
          v-if="item.file_path"
          icon
          small
          color="primary"
          @click="$emit('view-file', item.file_path, 'หลักฐานการขาดลามาสาย')"
        >
          <v-icon>{{ getFileIcon(item.file_path) }}</v-icon>
        </v-btn>
        <span v-else class="grey--text">ไม่มีไฟล์</span>
      </template>
      
      <template v-slot:item.created_at="{ item }">
        <span class="text-caption grey--text">
          {{ formatDateTime(item.created_at) }}
        </span>
      </template>
      
      <template v-slot:item.actions="{ item }">
        <v-btn icon small color="warning" @click="$emit('edit', item)" class="mr-1">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
        <v-btn icon small color="error" @click="$emit('delete', item)">
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Pagination -->
    <div class="text-center mt-4" v-if="totalPages > 1">
      <v-pagination
        v-model="page"
        :length="totalPages"
        :total-visible="7"
        color="primary"
      ></v-pagination>
    </div>

    <!-- Monthly Summary Table -->
    <v-card class="mt-6" v-if="summary.length > 0">
      <v-card-title class="success white--text">
        <v-icon left>mdi-chart-bar</v-icon>
        สรุปรายเดือน ปี {{ selectedYear }}
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="summaryHeaders"
          :items="summary"
          hide-default-footer
          :items-per-page="12"
          no-data-text="ไม่มีข้อมูลสรุป"
          class="summary-table"
        >
          <template v-slot:item.month_name="{ item }">
            <div class="font-weight-medium">{{ item.month_name }}</div>
          </template>
          
          <template v-slot:item.total_absent="{ item }">
            <v-chip small color="error" text-color="white" v-if="item.total_absent > 0">
              {{ item.total_absent }} วัน
            </v-chip>
            <span v-else class="grey--text">0</span>
          </template>
          
          <template v-slot:item.total_late="{ item }">
            <v-chip small color="warning" text-color="white" v-if="item.total_late > 0">
              {{ item.total_late }} ครั้ง
            </v-chip>
            <span v-else class="grey--text">0</span>
          </template>
          
          <template v-slot:item.total_early_leave="{ item }">
            <v-chip small color="primary" text-color="white" v-if="item.total_early_leave > 0">
              {{ item.total_early_leave }} ครั้ง
            </v-chip>
            <span v-else class="grey--text">0</span>
          </template>
          
          <template v-slot:item.total_late_minutes="{ item }">
            <span v-if="item.total_late_minutes > 0" class="warning--text font-weight-medium">
              {{ formatDuration(item.total_late_minutes) }}
            </span>
            <span v-else class="grey--text">0</span>
          </template>
          
          <template v-slot:item.total_early_minutes="{ item }">
            <span v-if="item.total_early_minutes > 0" class="primary--text font-weight-medium">
              {{ formatDuration(item.total_early_minutes) }}
            </span>
            <span v-else class="grey--text">0</span>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-card-text>
</template>

<script>
export default {
  name: 'AttendanceHistory',
  
  props: {
    attendances: {
      type: Array,
      default: () => []
    },
    summary: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      selectedMonth: null,
      selectedYear: new Date().getFullYear(),
      selectedType: null,
      selectedStatus: null,
      page: 1,
      itemsPerPage: 10,
      
      headers: [
        { text: 'วันที่', value: 'attendance_date', align: 'center', width: '120px' },
        { text: 'ประเภท', value: 'attendance_type', align: 'center', width: '120px' },
        { text: 'เวลาที่กำหนด', value: 'start_time', align: 'center', width: '100px' },
        { text: 'เวลาจริง', value: 'actual_time', align: 'center', width: '100px' },
        { text: 'ระยะเวลา', value: 'duration_minutes', align: 'center', width: '100px' },
        { text: 'เหตุผล', value: 'reason', width: '200px' },
        { text: 'สถานะ', value: 'status', align: 'center', width: '100px' },
        { text: 'ผู้อนุมัติ', value: 'approved_by', align: 'center', width: '120px' },
        { text: 'ไฟล์', value: 'file_path', align: 'center', width: '80px', sortable: false },
        { text: 'วันที่บันทึก', value: 'created_at', align: 'center', width: '120px' },
        { text: 'จัดการ', value: 'actions', align: 'center', width: '100px', sortable: false }
      ],
      
      summaryHeaders: [
        { text: 'เดือน', value: 'month_name', align: 'center', width: '120px' },
        { text: 'ขาดงาน', value: 'total_absent', align: 'center', width: '100px' },
        { text: 'มาสาย', value: 'total_late', align: 'center', width: '100px' },
        { text: 'กลับก่อน', value: 'total_early_leave', align: 'center', width: '100px' },
        { text: 'นาทีที่สาย', value: 'total_late_minutes', align: 'center', width: '120px' },
        { text: 'นาทีกลับก่อน', value: 'total_early_minutes', align: 'center', width: '120px' }
      ],
      
      monthOptions: [
        { text: 'มกราคม', value: 1 },
        { text: 'กุมภาพันธ์', value: 2 },
        { text: 'มีนาคม', value: 3 },
        { text: 'เมษายน', value: 4 },
        { text: 'พฤษภาคม', value: 5 },
        { text: 'มิถุนายน', value: 6 },
        { text: 'กรกฎาคม', value: 7 },
        { text: 'สิงหาคม', value: 8 },
        { text: 'กันยายน', value: 9 },
        { text: 'ตุลาคม', value: 10 },
        { text: 'พฤศจิกายน', value: 11 },
        { text: 'ธันวาคม', value: 12 }
      ],
      
      typeFilterOptions: [
        { text: 'ขาดงาน', value: 'absent' },
        { text: 'มาสาย', value: 'late' },
        { text: 'กลับก่อน', value: 'early_leave' }
      ],
      
      statusFilterOptions: [
        { text: 'รอพิจารณา', value: 'pending' },
        { text: 'อนุมัติ', value: 'approved' },
        { text: 'ไม่อนุมัติ', value: 'rejected' }
      ]
    }
  },
  
  computed: {
    yearOptions() {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let i = currentYear; i >= currentYear - 5; i--) {
        years.push({ text: i.toString(), value: i })
      }
      return years
    },
    
    filteredAttendances() {
      let filtered = [...this.attendances]
      
      if (this.selectedType) {
        filtered = filtered.filter(item => item.attendance_type === this.selectedType)
      }
      
      if (this.selectedStatus) {
        filtered = filtered.filter(item => item.status === this.selectedStatus)
      }
      
      return filtered
    },
    
    totalRecords() {
      return this.attendances.length
    },
    
    filteredRecords() {
      return this.filteredAttendances.length
    },
    
    totalPages() {
      return Math.ceil(this.filteredRecords / this.itemsPerPage)
    },
    
    summaryCards() {
      if (!this.summary.length) {
        return [
          { 
            label: 'ขาดงาน', 
            value: '0 วัน', 
            icon: 'mdi-close-circle', 
            color: 'red', 
            textClass: 'red--text', 
            class: '',
            subtext: 'เดือนนี้'
          },
          { 
            label: 'มาสาย', 
            value: '0 ครั้ง', 
            icon: 'mdi-clock-outline', 
            color: 'orange', 
            textClass: 'orange--text', 
            class: '',
            subtext: 'เดือนนี้'
          },
          { 
            label: 'กลับก่อน', 
            value: '0 ครั้ง', 
            icon: 'mdi-logout', 
            color: 'primary', 
            textClass: 'primary--text', 
            class: '',
            subtext: 'เดือนนี้'
          },
          { 
            label: 'รวมเวลา', 
            value: '0 นาที', 
            icon: 'mdi-timer', 
            color: 'blue', 
            textClass: 'blue--text', 
            class: '',
            subtext: 'ที่สาย/กลับก่อน'
          }
        ]
      }
      
      // หาข้อมูลเดือนปัจจุบันหรือเดือนที่เลือก
      const currentMonth = this.selectedMonth || new Date().getMonth() + 1
      const monthData = this.summary.find(s => s.summary_month === currentMonth) || {}
      
      const totalMinutes = (monthData.total_late_minutes || 0) + (monthData.total_early_minutes || 0)
      
      return [
        { 
          label: 'ขาดงาน', 
          value: `${monthData.total_absent || 0} วัน`, 
          icon: 'mdi-close-circle', 
          color: 'red', 
          textClass: 'red--text',
          class: monthData.total_absent > 0 ? 'red lighten-5' : '',
          subtext: monthData.total_absent > 3 ? 'มากผิดปกติ' : 'ปกติ',
          subtextClass: monthData.total_absent > 3 ? 'red--text' : 'grey--text'
        },
        { 
          label: 'มาสาย', 
          value: `${monthData.total_late || 0} ครั้ง`, 
          icon: 'mdi-clock-outline', 
          color: 'orange', 
          textClass: 'orange--text',
          class: monthData.total_late > 0 ? 'orange lighten-5' : '',
          subtext: `${monthData.total_late_minutes || 0} นาที`,
          subtextClass: 'orange--text'
        },
        { 
          label: 'กลับก่อน', 
          value: `${monthData.total_early_leave || 0} ครั้ง`, 
          icon: 'mdi-logout', 
          color: 'primary', 
          textClass: 'primary--text',
          class: monthData.total_early_leave > 0 ? 'primary' : '',
          subtext: `${monthData.total_early_minutes || 0} นาที`,
          subtextClass: 'primary--text'
        },
        { 
          label: 'รวมเวลา', 
          value: this.formatDuration(totalMinutes), 
          icon: 'mdi-timer', 
          color: 'blue', 
          textClass: 'blue--text',
          class: totalMinutes > 120 ? 'blue lighten-5' : '',
          subtext: totalMinutes > 480 ? 'มากเกินไป' : totalMinutes > 0 ? 'ปานกลาง' : 'ดีเยี่ยม',
          subtextClass: totalMinutes > 480 ? 'red--text' : totalMinutes > 0 ? 'orange--text' : 'green--text'
        }
      ]
    }
  },
  
  methods: {
    onFilterChange() {
      this.page = 1 // Reset to first page when filtering
      this.$emit('filter-changed', {
        month: this.selectedMonth,
        year: this.selectedYear,
        type: this.selectedType,
        status: this.selectedStatus
      })
    },
    
    getTypeColor(type) {
      switch (type) {
        case 'absent': return 'red'
        case 'late': return 'orange'
        case 'early_leave': return 'primary'
        default: return 'grey'
      }
    },
    
    getTypeIcon(type) {
      switch (type) {
        case 'absent': return 'mdi-close-circle'
        case 'late': return 'mdi-clock-outline'
        case 'early_leave': return 'mdi-logout'
        default: return 'mdi-help-circle'
      }
    },
    
    getTypeLabel(type) {
      switch (type) {
        case 'absent': return 'ขาดงาน'
        case 'late': return 'มาสาย'
        case 'early_leave': return 'กลับก่อน'
        default: return type
      }
    },
    
    getStatusColor(status) {
      switch (status) {
        case 'approved': return 'green'
        case 'rejected': return 'red'
        case 'pending': return 'orange'
        default: return 'grey'
      }
    },
    
    getStatusIcon(status) {
      switch (status) {
        case 'approved': return 'mdi-check-circle'
        case 'rejected': return 'mdi-close-circle'
        case 'pending': return 'mdi-clock-outline'
        default: return 'mdi-help-circle'
      }
    },
    
    getStatusLabel(status) {
      switch (status) {
        case 'approved': return 'อนุมัติ'
        case 'rejected': return 'ไม่อนุมัติ'
        case 'pending': return 'รอพิจารณา'
        default: return status
      }
    },
    
    getDurationColor(minutes) {
      if (minutes > 120) return 'red'
      if (minutes > 60) return 'orange'
      if (minutes > 30) return 'amber'
      return 'blue'
    },
    
    getFileIcon(filePath) {
      if (!filePath) return 'mdi-file'
      const ext = filePath.split('.').pop().toLowerCase()
      switch (ext) {
        case 'pdf': return 'mdi-file-pdf-box'
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif': return 'mdi-file-image'
        default: return 'mdi-file-document'
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      try {
        return new Date(dateString).toLocaleDateString('th-TH', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
      } catch {
        return dateString
      }
    },
    
    formatDateTime(dateString) {
      if (!dateString) return ''
      try {
        return new Date(dateString).toLocaleString('th-TH', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch {
        return dateString
      }
    },
    
    formatTime(timeString) {
      if (!timeString) return ''
      try {
        return timeString.slice(0, 5)
      } catch {
        return timeString
      }
    },
    
    formatDuration(minutes) {
      if (!minutes || minutes === 0) return '0 นาที'
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      
      if (hours > 0) {
        return mins > 0 ? `${hours} ช.ม. ${mins} น.` : `${hours} ช.ม.`
      }
      return `${mins} นาที`
    }
  }
}
</script>

<style scoped>
/* Custom styling for attendance history */
.reason-cell {
  max-width: 200px;
}

.summary-table .v-data-table__wrapper {
  border-radius: 0;
}

.v-chip.v-size--small {
  font-size: 11px;
  height: 20px;
}

.v-chip.v-size--x-small {
  font-size: 10px;
  height: 18px;
}

/* Summary cards hover effect */
.v-card:hover {
  transform: translateY(-2px);
  transition: all 0.3s ease;
}

/* Filter card styling */
.v-card--outlined {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

/* Pagination styling */
.v-pagination {
  justify-content: center;
}

/* Data table enhancements */
.v-data-table tbody tr:hover {
  background-color: rgba(33, 150, 243, 0.04) !important;
}

/* Summary card background variations */
.red.lighten-5 {
  background-color: #ffebee !important;
}

.orange.lighten-5 {
  background-color: #fff4dc !important;
}

.purple.lighten-5 {
  background-color: #fbecec !important;
}

.blue.lighten-5 {
  background-color: #f5f5f5 !important;
}

/* Mobile responsiveness */
@media (max-width: 600px) {
  .v-data-table {
    font-size: 12px;
  }
  
  .v-chip {
    font-size: 10px;
    height: 18px;
  }
}
</style>